// Procedurally generated demo survey: "Clearwater Reservoir" (fictional, deterministic seed).
export const GRID_W = 192;
export const GRID_H = 128;
export const CELL_FT = 32;
export const RENDER_SCALE = 4;
export const CONTOUR_FT = 5;
const MAX_DEPTH = 62;
const BASE_LAT = 44.8123;
const BASE_LON = -93.4567;
const FT_PER_DEG = 364000;

export const LAYERS = {
  depth: { key: "depth", label: "Depth", unit: "ft", stops: [[0, "#A5F3FC"], [8, "#38BDF8"], [20, "#0284C7"], [35, "#1E3A8A"], [50, "#172554"], [62, "#0B1033"]] },
  vegetation: { key: "vegetation", label: "Vegetation", unit: "% biovolume", stops: [[0, "#0F2A3D"], [15, "#14532D"], [45, "#16A34A"], [75, "#84CC16"], [100, "#D9F99D"]] },
  hardness: { key: "hardness", label: "Bottom Hardness", unit: "soft → hard", stops: [[0, "#3B2314"], [35, "#92400E"], [70, "#D4A054"], [100, "#F1E9D2"]] },
};
LAYERS.vegetation2025 = { ...LAYERS.vegetation, key: "vegetation2025", hidden: true };
export const SURVEY_YEARS = { before: "2024", after: "2025" };

const mulberry32 = (seed) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const makeNoise = (seed) => {
  const rnd = mulberry32(seed);
  const size = 64;
  const vals = new Float32Array(size * size);
  for (let i = 0; i < vals.length; i++) vals[i] = rnd();
  const at = (ix, iy) => vals[(((iy % size) + size) % size) * size + (((ix % size) + size) % size)];
  const sm = (t) => t * t * (3 - 2 * t);
  const base = (x, y) => {
    const x0 = Math.floor(x), y0 = Math.floor(y);
    const tx = sm(x - x0), ty = sm(y - y0);
    const top = at(x0, y0) * (1 - tx) + at(x0 + 1, y0) * tx;
    const bot = at(x0, y0 + 1) * (1 - tx) + at(x0 + 1, y0 + 1) * tx;
    return top * (1 - ty) + bot * ty;
  };
  return (x, y, oct = 4) => {
    let s = 0, amp = 1, f = 1, norm = 0;
    for (let o = 0; o < oct; o++) { s += base(x * f, y * f) * amp; norm += amp; amp *= 0.5; f *= 2; }
    return s / norm;
  };
};

const hexToRgb = (hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];

export const rampColor = (layer, v) => {
  const stops = LAYERS[layer].stops;
  if (v <= stops[0][0]) return hexToRgb(stops[0][1]);
  for (let i = 1; i < stops.length; i++) {
    if (v <= stops[i][0]) {
      const [v0, c0] = stops[i - 1], [v1, c1] = stops[i];
      const t = (v - v0) / (v1 - v0);
      const a = hexToRgb(c0), b = hexToRgb(c1);
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
    }
  }
  return hexToRgb(stops[stops.length - 1][1]);
};

export const rampCss = (layer) => {
  const s = LAYERS[layer].stops;
  const max = s[s.length - 1][0];
  return `linear-gradient(90deg, ${s.map(([v, c]) => `${c} ${(v / max) * 100}%`).join(", ")})`;
};

let cache = null;

const forEachCell = (fn) => {
  for (let y = 0; y < GRID_H; y++) for (let x = 0; x < GRID_W; x++) fn(x, y, y * GRID_W + x);
};
const norm = (x, y) => [(x / GRID_W - 0.5) * 2, (y / GRID_H - 0.5) * 2];
const clamp100 = (v) => Math.min(100, Math.max(0, v));

// Basin shape + raw depth field (0 = land).
const generateRawDepth = () => {
  const n1 = makeNoise(11), n2 = makeNoise(23);
  const raw = new Float32Array(GRID_W * GRID_H);
  forEachCell((x, y, i) => {
    const [nx, ny] = norm(x, y);
    const ang = Math.atan2(ny, nx);
    const r = Math.hypot(nx * 1.06, ny * 1.12);
    const shape = 0.84 + 0.13 * Math.sin(2 * ang + 0.8) + 0.09 * Math.sin(3 * ang - 1.7) + 0.05 * Math.sin(5 * ang + 2.5);
    const f = shape - r + (n1(x / 30, y / 30) - 0.5) * 0.38;
    if (f <= 0) return;
    let d = Math.pow(Math.min(f / 0.8, 1), 1.3) * MAX_DEPTH * (0.55 + 0.45 * n2(x / 20, y / 20));
    d *= 1 - 0.55 * Math.exp(-(((nx + 0.3) ** 2) + ((ny - 0.18) ** 2)) / 0.025);
    raw[i] = Math.max(0.6, d);
  });
  return raw;
};

// 3x3 mean over wet neighbours only.
const smoothDepth = (raw) => {
  const depth = new Float32Array(GRID_W * GRID_H);
  forEachCell((x, y, i) => {
    if (raw[i] <= 0) return;
    let s = 0, c = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const xx = x + dx, yy = y + dy;
        if (xx < 0 || yy < 0 || xx >= GRID_W || yy >= GRID_H) continue;
        const v = raw[yy * GRID_W + xx];
        if (v > 0) { s += v; c++; }
      }
    }
    depth[i] = Math.max(0.6, s / c);
  });
  return depth;
};

const TREATED_BAYS = [[0.85, 0.1, 0.16], [0.4, 0.7, 0.1], [0.3, -0.65, 0.08], [-0.8, 0.1, 0.09]];
const treatment = (nx, ny) =>
  Math.max(...TREATED_BAYS.map(([cx, cy, s]) => Math.exp(-(((nx - cx) ** 2) + ((ny - cy) ** 2)) / s)));

const generateLayers = (depth) => {
  const n3 = makeNoise(37), n4 = makeNoise(53), n5 = makeNoise(71);
  const vegetation = new Float32Array(GRID_W * GRID_H);
  const vegetation2025 = new Float32Array(GRID_W * GRID_H);
  const hardness = new Float32Array(GRID_W * GRID_H);
  forEachCell((x, y, i) => {
    const d = depth[i];
    if (d <= 0) return;
    const shallow = Math.max(0, 1 - d / 17);
    vegetation[i] = clamp100(shallow * (n3(x / 12, y / 12) * 1.9 - 0.45) * 130);
    const [nx, ny] = norm(x, y);
    const kill = 1 - 0.92 * Math.min(1, treatment(nx, ny) * 1.3);
    vegetation2025[i] = clamp100(vegetation[i] * kill * (0.7 + 0.5 * n5(x / 14, y / 14)));
    hardness[i] = clamp100((0.15 + 0.75 * Math.pow(1 - d / MAX_DEPTH, 1.6) + (n4(x / 16, y / 16) - 0.5) * 0.5) * 100);
  });
  return { vegetation, vegetation2025, hardness };
};

const computeStats = ({ depth, vegetation, vegetation2025 }) => {
  let wet = 0, sum = 0, max = 0, vegCells = 0, vegCells2025 = 0, vegSum = 0, vegSum2025 = 0;
  forEachCell((_x, _y, i) => {
    const d = depth[i];
    if (d <= 0) return;
    wet++; sum += d; max = Math.max(max, d);
    vegSum += vegetation[i]; vegSum2025 += vegetation2025[i];
    if (vegetation[i] > 20) vegCells++;
    if (vegetation2025[i] > 20) vegCells2025++;
  });
  const cellAcres = (CELL_FT * CELL_FT) / 43560;
  return {
    acres: wet * cellAcres,
    maxDepth: max,
    meanDepth: sum / wet,
    volumeAcreFt: sum * cellAcres,
    vegCoverage: (vegCells / wet) * 100,
    vegCoverage2025: (vegCells2025 / wet) * 100,
    vegAcres: vegCells * cellAcres,
    vegAcres2025: vegCells2025 * cellAcres,
    meanBiovolume: vegSum / wet,
    meanBiovolume2025: vegSum2025 / wet,
    transects: 34,
    points: wet * 41,
  };
};

export function getLake() {
  if (cache) return cache;
  const depth = smoothDepth(generateRawDepth());
  const layers = generateLayers(depth);
  cache = { depth, ...layers, stats: computeStats({ depth, ...layers }) };
  return cache;
}

export function sample(grid, gx, gy) {
  const x = Math.min(Math.max(gx - 0.5, 0), GRID_W - 1.001);
  const y = Math.min(Math.max(gy - 0.5, 0), GRID_H - 1.001);
  const x0 = Math.floor(x), y0 = Math.floor(y), tx = x - x0, ty = y - y0;
  const a = grid[y0 * GRID_W + x0], b = grid[y0 * GRID_W + x0 + 1];
  const c = grid[(y0 + 1) * GRID_W + x0], d = grid[(y0 + 1) * GRID_W + x0 + 1];
  return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
}

export function readAt(gx, gy) {
  const lake = getLake();
  if (gx < 0 || gy < 0 || gx >= GRID_W || gy >= GRID_H) return null;
  const depth = sample(lake.depth, gx, gy);
  const lat = BASE_LAT - (gy * CELL_FT) / FT_PER_DEG;
  const lon = BASE_LON + (gx * CELL_FT) / (FT_PER_DEG * Math.cos((lat * Math.PI) / 180));
  return {
    gx, gy, lat, lon,
    water: depth > 0.3,
    depth,
    vegetation: sample(lake.vegetation, gx, gy),
    vegetation2025: sample(lake.vegetation2025, gx, gy),
    hardness: sample(lake.hardness, gx, gy),
  };
}

export function profileBetween(a, b, n = 90) {
  const lake = getLake();
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const gx = a.gx + (b.gx - a.gx) * t, gy = a.gy + (b.gy - a.gy) * t;
    pts.push({ dist: Math.hypot(b.gx - a.gx, b.gy - a.gy) * CELL_FT * t, depth: sample(lake.depth, gx, gy) });
  }
  return pts;
}

const LAND = [19, 27, 46];
const CONTOUR = [3, 8, 20];
const SHORE = [103, 232, 249];

export function renderLayer(layer) {
  const lake = getLake();
  const W = GRID_W * RENDER_SCALE, H = GRID_H * RENDER_SCALE;
  const grid = lake[layer];
  const img = new ImageData(W, H);
  const data = img.data;
  const dAt = (px, py) => sample(lake.depth, (px + 0.5) / RENDER_SCALE, (py + 0.5) / RENDER_SCALE);
  const tex = makeNoise(7);
  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      const d = dAt(px, py);
      let rgb;
      if (d <= 0.3) {
        const t = tex(px / 40, py / 40, 3) * 14;
        rgb = [LAND[0] + t, LAND[1] + t, LAND[2] + t];
        const nb = Math.max(dAt(px + 1, py), dAt(px, py + 1), dAt(px - 1, py), dAt(px, py - 1));
        if (nb > 0.3) rgb = SHORE;
      } else {
        rgb = rampColor(layer, layer === "depth" ? d : sample(grid, (px + 0.5) / RENDER_SCALE, (py + 0.5) / RENDER_SCALE));
        const band = Math.floor(d / CONTOUR_FT);
        const r = dAt(px + 1, py), dn = dAt(px, py + 1);
        if ((r > 0.3 && Math.floor(r / CONTOUR_FT) !== band) || (dn > 0.3 && Math.floor(dn / CONTOUR_FT) !== band)) {
          const major = band % 2 === 1;
          const k = major ? 0.7 : 0.45;
          rgb = [rgb[0] * (1 - k) + CONTOUR[0] * k, rgb[1] * (1 - k) + CONTOUR[1] * k, rgb[2] * (1 - k) + CONTOUR[2] * k];
        }
      }
      const o = (py * W + px) * 4;
      data[o] = rgb[0]; data[o + 1] = rgb[1]; data[o + 2] = rgb[2]; data[o + 3] = 255;
    }
  }
  return img;
}
